import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { Plus, X } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import { User } from '../../types';
import { useAuth } from '../../../../contexts/AuthContext';
import { formatDateForInput } from '../../../../utils/date.utils';

interface AddActionItemFormProps {
  rockId: string;
  onAdd: (task: string, assigneeId: string | null, dueDate: string) => Promise<void>;
  onSuccess?: () => void;
  onCancel?: () => void;
  users?: User[]; // Optional - component now uses eosUsers from AuthContext
}

/**
 * Inline form for adding a new action item (to-do) to a rock
 * Uses Formik for form state and Yup for validation
 */
export function AddActionItemForm({
  rockId,
  onAdd,
  onSuccess,
  onCancel,
  users,
}: AddActionItemFormProps) {
  const { eosUsers = [] } = useAuth();
  const toast = useToast();

  // Color mode values for dark mode support
  const formBg = useColorModeValue('gray.50', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');

  const validationSchema = Yup.object({
    task: Yup.string()
      .required('Task is required')
      .min(3, 'Task must be at least 3 characters'),
    assigneeId: Yup.string().nullable(),
    dueDate: Yup.string().required('Due date is required'),
  });

  // Calculate default due date (7 days from now)
  const getDefaultDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return formatDateForInput(date); // Format as YYYY-MM-DD without timezone shift
  };

  const handleSubmit = async (
    values: { task: string; assigneeId: string; dueDate: string },
    { setSubmitting, resetForm }: any
  ) => {
    try {
      // Format due date: append T12:00:00 to avoid timezone shift
      const formattedDueDate = values.dueDate.includes('T') 
        ? values.dueDate 
        : `${values.dueDate}T12:00:00`;
      
      await onAdd(values.task, values.assigneeId || null, formattedDueDate);
      toast({
        title: 'Action item added',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      resetForm();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add action item',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3} bg={formBg} borderRadius="md" mb={2}>
      <Formik
        initialValues={{ task: '', assigneeId: '', dueDate: getDefaultDueDate() }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <HStack spacing={2} align="flex-end" wrap="wrap">
              <Field name="task">
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.task && form.touched.task} flex={2} minW="200px">
                    <Input
                      {...field}
                      placeholder="To-do description"
                      size="sm"
                      bg={inputBg}
                    />
                    <FormErrorMessage fontSize="xs">{form.errors.task}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="assigneeId">
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.assigneeId && form.touched.assigneeId} flex={1} minW="150px">
                    <Select {...field} placeholder="Assign to (optional)" size="sm" bg={inputBg}>
                      {eosUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage fontSize="xs">{form.errors.assigneeId}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="dueDate">
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.dueDate && form.touched.dueDate} flex={1} minW="150px">
                    <Input
                      {...field}
                      type="date"
                      size="sm"
                      bg={inputBg}
                    />
                    <FormErrorMessage fontSize="xs">{form.errors.dueDate}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Button
                type="submit"
                size="sm"
                colorScheme="blue"
                leftIcon={<Icon as={Plus} w={4} h={4} />}
                isLoading={isSubmitting}
                loadingText="Adding..."
              >
                Add
              </Button>
              {onCancel && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCancel}
                  aria-label="Cancel"
                >
                  <Icon as={X} w={4} h={4} />
                </Button>
              )}
            </HStack>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
